import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useLayoutEffect, type ReactNode, type RefObject } from 'react';
import { cn } from '@/utils/cn';

interface FloatingListboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  id: string;
  className?: string;
}

/**
 * Viewport-aware listbox surface shared by all form selection controls.
 * It is portalled to the document body so scroll containers cannot clip it.
 */
export function FloatingListbox({
  open,
  onOpenChange,
  anchorRef,
  children,
  id,
  className,
}: FloatingListboxProps) {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8, fallbackStrategy: 'bestFit' }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, availableWidth, rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${Math.min(availableWidth, Math.max(224, rects.reference.width))}px`,
            maxHeight: `${Math.min(300, Math.max(120, availableHeight))}px`,
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context, {
    escapeKey: false,
    outsidePressEvent: 'mousedown',
  });
  const { getFloatingProps } = useInteractions([dismiss]);

  useLayoutEffect(() => {
    if (open && anchorRef.current) {
      const scrollRegion = anchorRef.current.closest<HTMLElement>('[data-modal-scroll-region]');
      if (scrollRegion) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const regionRect = scrollRegion.getBoundingClientRect();
        const edgePadding = 12;
        const delta =
          anchorRect.bottom > regionRect.bottom - edgePadding
            ? anchorRect.bottom - regionRect.bottom + edgePadding
            : anchorRect.top < regionRect.top + edgePadding
              ? anchorRect.top - regionRect.top - edgePadding
              : 0;
        if (delta !== 0) {
          scrollRegion.scrollBy({
            top: delta,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth',
          });
        }
      }
      refs.setReference(anchorRef.current);
    }
  }, [anchorRef, open, refs]);

  if (!open) return null;

  const dialogOwner = anchorRef.current
    ?.closest<HTMLElement>('[data-modal-id]')
    ?.dataset.modalId;

  return (
    <FloatingPortal preserveTabOrder>
      <FloatingFocusManager
        context={context}
        modal={false}
        initialFocus={-1}
        returnFocus={false}
      >
        <div
          {...getFloatingProps({
            id,
            ref: refs.setFloating,
            style: floatingStyles,
            className: cn(
              'z-[300] flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800',
              className,
            ),
          })}
          data-dialog-owner={dialogOwner}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
